import { create } from 'zustand'
import type { RoturaFilter, PendingStatusFilter, SortKey } from '@/types'
import type { ComponentStatus } from '@/types/productionSequence'
import type { ItemType } from '@/utils/itemType'

export interface ViewFilters {
  // TRES buscadores, uno por tipo de cosa. Antes era uno solo que miraba a la
  // vez el nº de orden, el artículo fabricado y los componentes consumidos: sus
  // términos se combinaban con OR, así que pedir «esta orden» y «este
  // componente» devolvía las dos cosas por separado en vez de su cruce. Dentro
  // de cada campo sigue habiendo OR; entre campos hay AND.
  /** Nº de orden de producción. */
  orden: string[]
  /** Artículo que la orden FABRICA, por código o por nombre. */
  articulo: string[]
  /** Componente que la orden CONSUME: la orden entra si alguna de sus líneas de
   *  plan casa. Es lo que hace útil buscar una materia prima y ver qué
   *  producciones se la comen. */
  componente: string[]
  estados: Set<number>
  rutasSelec: Set<string>
  empresaSelec: Set<string>   // multi-empresa filter
  tipoArticulo: Set<ItemType>   // filtro por prefijo Q/P/X; vacío = todos
  /** Semáforo de material: qué estados se enseñan. Vacío = todos.
   *  Sustituye en la práctica al binario `rotura`, que sólo sabía distinguir
   *  «se puede producir» de «no», sin ver que entre medias hay dos casos que se
   *  arreglan de maneras muy distintas: mover material o llamar al proveedor. */
  materialSelec: Set<ComponentStatus>
  rotura: RoturaFilter
  /** Matiz de `rotura: 'con'`: la rotura tiene que ser DEL componente buscado,
   *  no de cualquiera de los que consume la orden. Sólo tiene efecto cuando hay
   *  componente en el buscador; sin él se ignora, para que no filtre a
   *  escondidas algo que la pantalla no está enseñando. */
  roturaDelComponente: boolean
  pendingStatus: PendingStatusFilter  // filter by unidades_pendientes
  retrasadas: boolean
  almacenTab: string
  opMin: number
  opMax: number
  sort: SortKey
  sortDir: 'asc' | 'desc'
  allExpanded: boolean
}

const defaultFilters = (): ViewFilters => ({
  orden: [], articulo: [], componente: [],
  estados: new Set(), rutasSelec: new Set(), empresaSelec: new Set(),
  tipoArticulo: new Set(), materialSelec: new Set(),
  rotura: 'all', roturaDelComponente: false, pendingStatus: 'all', retrasadas: false, almacenTab: '',
  opMin: 0, opMax: 9999, sort: 'fecha', sortDir: 'asc', allExpanded: true,
})

function loadBool(key: string, def: boolean): boolean {
  try { const v = localStorage.getItem(key); return v === null ? def : v !== 'false' } catch { return def }
}

interface AppStore {
  // Offset de navegación por rango del Gantt. Uno por vista para que cambiar de
  // rango y volver no pierda la posición (antes el mensual vivía en estado local
  // de la página y se reseteaba al desmontarla).
  weekOffset: number
  setWeekOffset: (offset: number) => void
  monthOffset: number
  setMonthOffset: (offset: number) => void
  yearOffset: number
  setYearOffset: (offset: number) => void
  dark: boolean
  setDark: (v: boolean) => void
  sidebarCollapsed: boolean
  setSidebarCollapsed: (v: boolean) => void
  // Filtros COMPARTIDOS entre la vista semanal y la mensual de Planificación
  // Avanzada (se mantienen al cambiar de modo con el selector).
  planFilters: ViewFilters
  setPlanFilters: (partial: Partial<ViewFilters>) => void
}

export const useAppStore = create<AppStore>((set) => ({
  weekOffset: 0,
  setWeekOffset: (offset) => set({ weekOffset: offset }),

  monthOffset: 0,
  setMonthOffset: (offset) => set({ monthOffset: offset }),

  yearOffset: 0,
  setYearOffset: (offset) => set({ yearOffset: offset }),

  dark: loadBool('airval-dark', false),
  setDark: (v) => {
    try { localStorage.setItem('airval-dark', String(v)) } catch {}
    set({ dark: v })
  },

  sidebarCollapsed: loadBool('airval-sidebar-collapsed', false),
  setSidebarCollapsed: (v) => {
    try { localStorage.setItem('airval-sidebar-collapsed', String(v)) } catch {}
    set({ sidebarCollapsed: v })
  },

  planFilters: defaultFilters(),
  setPlanFilters: (partial) => set(state => ({ planFilters: { ...state.planFilters, ...partial } })),
}))
